import {type Middleware} from 'redux';
import {createSelector} from 'reselect';

import {type RootAction, type RootState} from '../../types/redux';
import {recordRumAction} from '../../util/datadog/datadogRum';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../util/panelbankTypes';
import {type PanelBankConfigNormalized} from './panelBankConfig/types';
import {type PanelsNormalized} from './panels/types';
import {type ViewReducerState} from './reducerSupport';
import {type RunPageNormalized} from './runPage/types';
import {
  isSaveableAction,
  isViewPartsUpdateAction,
  isViewPartUpdateAction,
} from './sagas';
import {type SectionNormalized} from './section/types';

export const workspaceTelemetryMiddleware: Middleware<
  {},
  Pick<RootState, 'views'>
> = store => next => action => {
  const viewAction = action as RootAction;
  if (
    action == null ||
    typeof action !== 'object' ||
    !('type' in action) ||
    typeof action.type !== 'string' ||
    !isSaveableAction(viewAction)
  ) {
    return next(action);
  }

  const viewID = isViewPartUpdateAction(viewAction)
    ? viewAction.payload.ref.viewID
    : isViewPartsUpdateAction(viewAction)
      ? viewAction.payload.refs[0]?.viewID
      : undefined;
  if (typeof viewID !== 'string') {
    return next(action);
  }

  const prevViewsState = store.getState().views;
  const result = next(action); // order matters here: we need to get the next state before we check if the parts have changed
  const currentViewsState = store.getState().views;

  // Reducers preserve this reference when no normalized view-spec data changes
  if (prevViewsState.parts === currentViewsState.parts) {
    return result;
  }

  const previous = selectWorkspaceSnapshot(prevViewsState, viewID);
  const current = selectWorkspaceSnapshot(currentViewsState, viewID);
  if (previous == null && current == null) {
    return result;
  }

  recordRumAction('workspace.redux_state', {
    workspaceReduxState: {
      actionType: viewAction.type,
      current,
      previous,
    },
  });

  return result;
};

// selectors to help us build the telemetry object

function selectWorkspacePart(
  viewsState: ViewReducerState,
  viewID: string
): RunPageNormalized | SectionNormalized | undefined {
  const viewRef = viewsState.views[viewID]?.partRef;
  if (viewRef == null) {
    return undefined;
  }

  const parts = viewsState.parts;
  if (viewRef.type === 'run-view') {
    return parts['run-view'][viewRef.id];
  }

  if (
    viewRef.type !== 'project-view' &&
    viewRef.type !== 'group-view' &&
    viewRef.type !== 'sweep-view'
  ) {
    return undefined;
  }

  const view = parts[viewRef.type][viewRef.id];
  return view == null ? undefined : parts.section[view.sectionRef.id];
}

const selectWorkspacePanelBankConfig = createSelector(
  selectWorkspacePart,
  (viewsState: ViewReducerState) => viewsState.parts['panel-bank-config'],
  (workspacePart, panelBankConfigs) =>
    workspacePart == null
      ? undefined
      : panelBankConfigs[workspacePart.panelBankConfigRef.id]
);

const selectWorkspacePanels = createSelector(
  selectWorkspacePart,
  (viewsState: ViewReducerState) => viewsState.parts.panels,
  (workspacePart, panels) =>
    workspacePart?.panelsRef == null
      ? undefined
      : panels[workspacePart.panelsRef.id]
);

const selectWorkspaceSettings = createSelector(
  selectWorkspacePart,
  (viewsState: ViewReducerState) => viewsState.parts['workspace-settings'],
  (workspacePart, workspaceSettings) =>
    workspacePart == null
      ? undefined
      : workspaceSettings[workspacePart.workspaceSettingsRef.id]
);

const selectSectionRefs = createSelector(
  selectWorkspacePanelBankConfig,
  panelBankConfig => panelBankConfig?.sectionRefs
);

/**
 * Narrow inputs let Reselect reuse counts when unrelated workspace state changes.
 * https://reselect.js.org/api/createSelector
 */
const selectWorkspaceTotals = createSelector(
  selectSectionRefs,
  selectWorkspacePanels,
  (viewsState: ViewReducerState) =>
    viewsState.parts['panel-bank-section-config'],
  (sectionRefs, panels, sections) =>
    sectionRefs == null
      ? undefined
      : workspaceTotals(sectionRefs, panels, sections)
);

const selectWorkspaceSnapshot = createSelector(
  selectWorkspaceTotals,
  selectWorkspaceSettings,
  (totals, workspaceSettings) =>
    totals == null || workspaceSettings == null
      ? undefined
      : {...totals, workspaceSettings}
);

function workspaceTotals(
  sectionRefs: PanelBankConfigNormalized['sectionRefs'],
  panels: PanelsNormalized | undefined,
  sections: ViewReducerState['parts']['panel-bank-section-config']
) {
  // These are stored totals, including any Hidden Panels section and its panels.
  const workspaceSectionRefs = sectionRefs ?? [];
  // Match the display path, which recognizes Hidden Panels only in the last slot.
  const hiddenSectionRef =
    workspaceSectionRefs[workspaceSectionRefs.length - 1];
  const hiddenSection =
    hiddenSectionRef == null ? undefined : sections[hiddenSectionRef.id];
  const hiddenPanels =
    hiddenSection?.name === PANEL_BANK_HIDDEN_SECTION_NAME
      ? hiddenSection.panelRefs.length
      : 0;

  let totalPanels = 0;
  // Read each section's array length without visiting or copying its panels.
  for (const ref of workspaceSectionRefs) {
    totalPanels += sections[ref.id]?.panelRefs?.length ?? 0;
  }

  if (panels != null) {
    for (const key in panels.views) {
      totalPanels += panels.views[key]?.panelRefs?.length ?? 0;
    }
  }

  return {
    hiddenPanels,
    totalPanels,
    totalSections: workspaceSectionRefs.length,
  };
}
